import { cn } from "@-/com.npmjs.cn";
import filledArray from "@-/com.npmjs.filled-array";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeStagesByGrapheme } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import type { CardTileType } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile";
import { CardDetailsTileLink } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile.link.details";
import chunk from "@-/com.npmjs.just-split";
import { useMemo, useState, type ReactElement } from "react";
import { Platform, View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { HeaderLegendList } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import { useSectionAccordionRowWindow } from "@-/dev.improve.haniverse.components.ui.section-accordion";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import type { UserCardSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import arrayLength from "@-/com.npmjs.extra-array__length";
import {
  computeTileSize,
  computeColumnCount,
  DEFAULT_COLUMN_COUNT,
  GRID_GAP,
  GRID_HORIZONTAL_PADDING,
  MAX_TILE_SIZE,
} from "#card-details-tile-grid-metrics";

/** One Meaning or Pronounce card tile. A morpheme contributes one tile per studied card. */
export type CardDetailsTileData = {
  cardType: CardTileType;
  /** Pre-formatted due label (e.g. `"5d"`); omit for unscheduled cards. */
  dueLabel?: string;
  graphemeCard: MorphemeDetailsTileSlice;
  /** `graphemeCard` is provisional (actor-DB only) — see `CardDetailsTileUiProps.isDetailsPending`. */
  isDetailsPending?: boolean;
  isMastered?: boolean;
  /** A pronunciation card's own tone-number reading; see `CardDetailsTileUiProps.pinyin`. */
  pinyin?: string | null;
  selectionItem: UserCardSelectionItem;
};

type ListRow = { tiles: CardDetailsTileData[] };

// `@legendapp/list`'s web build spreads any unrecognized prop straight onto the
// underlying scroll `div`, so the native-only `keyboardShouldPersistTaps` /
// `keyboardDismissMode` ScrollView props leak as unknown DOM attributes and
// trigger React's "does not recognize the prop" warning. They're meaningless on
// web anyway, so omit them there and keep them only on native.
const keyboardScrollProps = (keyboardDismissMode: "none" | "on-drag" | "interactive") =>
  Platform.OS === "web"
    ? {}
    : ({ keyboardDismissMode, keyboardShouldPersistTaps: "handled" } as const);

// A character can own several cards of the same `cardType` (e.g. two
// pronunciation cards for two readings), so `${id}-${cardType}` alone collides.
// The tile's position within its row disambiguates those siblings.
function tileKey(tile: CardDetailsTileData, index: number) {
  return `${tile.selectionItem.userCardId}-${index}`;
}

export function CardDetailsTileList({
  bottomInset = 0,
  columnCount = DEFAULT_COLUMN_COUNT,
  href,
  isEditing = false,
  keyboardDismissMode = "on-drag",
  ListFooterComponent,
  onEndReached,
  onGraphemePress,
  onRemoveCard,
  padding = GRID_HORIZONTAL_PADDING,
  scrollable = true,
  stageByGrapheme,
  tiles,
}: {
  bottomInset?: number;
  columnCount?: number;
  /**
   * The morpheme-panels route prefix a tapped tile opens, forwarded to every
   * {@link CardDetailsTileLink} so the pushed panel stays inside the hosting tab's stack.
   */
  href: string;
  isEditing?: boolean;
  keyboardDismissMode?: "none" | "on-drag" | "interactive";
  ListFooterComponent?: ReactElement | null;
  onEndReached?: () => void;
  onGraphemePress?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  onRemoveCard?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  /** Outer inset around the grid, also used by its tile and row measurements. */
  padding?: number;
  scrollable?: boolean;
  /**
   * Every tile's SRS stage, supplied by the caller (the deck derives stages from the same actor
   * rows it renders from, so the grid paints from the caller's data alone). Sparse: a front absent
   * from the map is not in the deck, i.e. `"unadded"`.
   */
  stageByGrapheme: MorphemeStagesByGrapheme;
  tiles: CardDetailsTileData[];
}) {
  const [windowWidth] = useContextSelectors(RootContext, (c) => c.windowDimensions.width);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  // The grid may sit next to sidebars (on web especially), so `windowWidth`
  // can overstate the available content width. Prefer the post-mount
  // `onLayout` measurement; fall back to `windowWidth` so the first paint
  // isn't empty. The first paint can briefly overflow if the container is
  // narrower than the window, but `onLayout` corrects it within a frame.
  const widthForLayout = containerWidth ?? windowWidth;
  const effectiveColumnCount = computeColumnCount(
    widthForLayout,
    columnCount,
    MAX_TILE_SIZE,
    padding,
  );
  const tileSize = computeTileSize(widthForLayout, effectiveColumnCount, padding);
  const rows = useMemo<ListRow[]>(
    () => chunk(tiles, effectiveColumnCount).map((row): ListRow => ({ tiles: row })),
    [tiles, effectiveColumnCount],
  );
  const rowHeight = tileSize + GRID_GAP;
  const rowWindow = useSectionAccordionRowWindow({
    enabled: !scrollable,
    offsetTop: padding,
    rowCount: rows.length,
    rowGap: GRID_GAP,
    rowHeight,
  });
  const rowsHeight = Math.max(0, rows.length * rowHeight - GRID_GAP);
  const topSpacerHeight = Math.min(rowsHeight, rowWindow.start * rowHeight);
  const bottomSpacerHeight = Math.max(0, rowsHeight - rowWindow.end * rowHeight);

  // The grid owns one navigator; each tile still owns its selection and pending treatment.
  // Previously every tile created push/replace/replaceImmediately/navigate mutation observers
  // and all the destination query factories even though only push is used after a press.
  const {
    push: { mutateAsync: pushCard },
  } = useMorphemePanelsHrefNavigator();
  const navigateCard = (pressedCard: MorphemeDetailsTileSlice) =>
    pushCard({
      morpheme: pressedCard.front,
      prefix: { href, params: { tab: "progress" } },
    });

  // The inter-row gap lives below every row except the last so the grid's own
  // `paddingTop`/`paddingBottom` stay symmetric — otherwise the last row's
  // trailing gap stacks onto `paddingBottom` and the bottom inset reads larger
  // than the top.
  const renderRow = (item: ListRow, rowKey: string, isLastRow: boolean) => (
    <View className={cn("flex-row gap-1", !isLastRow && "pb-1")} key={rowKey}>
      {item.tiles.map((tile, tileIndex) => (
        <View
          className="relative overflow-hidden"
          key={tileKey(tile, tileIndex)}
          style={{ height: tileSize, width: tileSize }}
        >
          <CardDetailsTileLink
            cardType={tile.cardType}
            dueLabel={tile.dueLabel}
            graphemeCard={tile.graphemeCard}
            href={href}
            isDetailsPending={tile.isDetailsPending}
            isEditing={isEditing}
            isMastered={tile.isMastered}
            onGraphemePress={onGraphemePress}
            onNavigate={navigateCard}
            onRemoveCard={onRemoveCard}
            pinyin={tile.pinyin}
            selectionItem={tile.selectionItem}
            size={tileSize}
            stage={stageByGrapheme[tile.graphemeCard.front] ?? "unadded"}
            // Stable direct-Pressable target for end-to-end tests. Exact user-card identity lives
            // in `selectionItem` and the React key; keep this public ID content-based because actor
            // UUIDs vary by run. The tile's own text is not reliably matchable because morphemes
            // render as separately colored spans and definitions truncate.
            testID={`card-details-tile-${tile.cardType}-${tile.graphemeCard.front}`}
          />
        </View>
      ))}
      {filledArray(
        (index) => (
          <View
            className="relative overflow-hidden"
            key={`card-details-tile-placeholder-${index}`}
            style={{ height: tileSize, width: tileSize }}
          />
        ),
        effectiveColumnCount - arrayLength(item.tiles),
      )}
    </View>
  );

  const handleLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    const measuredWidth = event.nativeEvent.layout.width;
    if (measuredWidth !== containerWidth && measuredWidth > 0) {
      setContainerWidth(measuredWidth);
    }
  };

  if (!scrollable) {
    return (
      <View
        onLayout={handleLayout}
        style={{
          paddingBottom: padding + bottomInset,
          paddingHorizontal: padding,
          paddingTop: padding,
        }}
        testID="card-details-tile-list"
      >
        {topSpacerHeight > 0 ? (
          <View style={{ height: topSpacerHeight }} testID="card-details-tile-list-leading-space" />
        ) : null}
        {rows
          .slice(rowWindow.start, rowWindow.end)
          .map((row, windowIndex) =>
            renderRow(
              row,
              row.tiles.map((tile, tileIndex) => tileKey(tile, tileIndex)).join(","),
              rowWindow.start + windowIndex === arrayLength(rows) - 1,
            ),
          )}
        {bottomSpacerHeight > 0 ? (
          <View
            style={{ height: bottomSpacerHeight }}
            testID="card-details-tile-list-trailing-space"
          />
        ) : null}
        {ListFooterComponent}
      </View>
    );
  }

  return (
    <View className="flex-1" onLayout={handleLayout}>
      <HeaderLegendList
        className="flex-1"
        contentContainerStyle={{
          paddingBottom: padding + bottomInset,
          paddingHorizontal: padding,
          paddingTop: padding,
        }}
        contentInsetAdjustmentBehavior="automatic"
        data={rows}
        {...keyboardScrollProps(keyboardDismissMode)}
        keyExtractor={(row) =>
          row.tiles.map((tile, tileIndex) => tileKey(tile, tileIndex)).join(",")
        }
        ListFooterComponent={ListFooterComponent}
        maintainVisibleContentPosition
        onEndReached={onEndReached}
        onEndReachedThreshold={0.5}
        recycleItems
        renderItem={({ index, item }) =>
          renderRow(
            item,
            item.tiles.map((tile, tileIndex) => tileKey(tile, tileIndex)).join(","),
            index === arrayLength(rows) - 1,
          )
        }
      />
    </View>
  );
}
